import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import useFormInstance from "antd/es/form/hooks/useFormInstance";
import { LabeledValue } from "antd/es/select";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import {
  Company,
  Deal,
  EntityTypesSupportingCompanyAssociation,
  Person,
} from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { EntityTypeShapedIcon, FormItem, SelectField } from "@mapmycustomers/ui";

import CompanyAssociation from "@app/component/associations/CompanyAssociation";
import ChangeAssociationsPayload from "@app/store/associations/ChangeAssociationsPayload";
import { fetchCompany } from "@app/store/company/actions";
import showRelateEntitiesAlert from "@app/util/form/showRelateEntitiesAlert";

import styles from "./CommonRelationshipField.module.scss";
import RelateEntitiesRequestPayload from "./RelateEntitiesRequestPayload";

interface Props {
  allowAdd?: boolean;
  allowClear?: boolean;
  associatedCompany?: Company;
  associatedDeal?: Deal;
  associatedPerson?: Person;
  className?: string;
  disabled?: boolean;
  entityType: EntityTypesSupportingCompanyAssociation;
  hideRelateEntitiesAlert?: boolean;
  label: ReactNode;
  loading?: boolean;
  onChangeAssociatedEntities: (payload: Omit<ChangeAssociationsPayload, "entityType">) => void;
  onFetchCompany: typeof fetchCompany;
  relateEntities?: (payload: RelateEntitiesRequestPayload) => void;
  required?: boolean;
  suggestedCompanies?: Company[];
  zIndex?: number;
}

const CompanyRelationshipField = ({
  allowAdd,
  allowClear,
  associatedCompany,
  associatedDeal,
  associatedPerson,
  className,
  disabled,
  entityType,
  hideRelateEntitiesAlert,
  label,
  onChangeAssociatedEntities,
  onFetchCompany,
  relateEntities,
  required,
  suggestedCompanies,
  zIndex,
}: Props) => {
  const form = useFormInstance();
  const intl = useIntl();

  const [modalVisible, showModal, hideModal] = useBoolean();
  const [selectedCompany, setSelectedCompany] = useState(associatedCompany);

  const handleSelect = useCallback(
    (companyIds: Company["id"][]) => {
      const companyId = companyIds[0];
      onFetchCompany({
        id: companyId,
        callback: (company: Company) => {
          setSelectedCompany(company);
          form.setFieldValue("companyId", companyId);
          const assignee = form.getFieldValue("assignee");
          onChangeAssociatedEntities({
            assignee,
            company,
            deal: associatedDeal,
            person: associatedPerson,
          });

          if (hideRelateEntitiesAlert) {
            showRelateEntitiesAlert(
              intl,
              assignee,
              company,
              associatedPerson,
              associatedDeal,
              relateEntities
            );
          }
        },
        options: { includeAccessStatus: true, includeUsersWithAccess: true },
      });
    },
    [
      associatedDeal,
      associatedPerson,
      form,
      hideRelateEntitiesAlert,
      intl,
      onChangeAssociatedEntities,
      onFetchCompany,
      relateEntities,
    ]
  );

  const handleRemove = useCallback(() => {
    onChangeAssociatedEntities({
      assignee: form.getFieldValue("assignee"),
      company: undefined,
      deal: associatedDeal,
      person: associatedPerson,
    });
    form.setFieldValue("companyId", undefined);
  }, [associatedDeal, associatedPerson, form, onChangeAssociatedEntities]);

  const options = useMemo<LabeledValue[]>(
    () =>
      selectedCompany
        ? [
            {
              key: String(selectedCompany.id),
              label: selectedCompany.name,
              value: selectedCompany.id,
            },
          ]
        : [],
    [selectedCompany]
  );

  useEffect(() => {
    setSelectedCompany(associatedCompany);
    form.setFieldValue("companyId", associatedCompany?.id);
  }, [associatedCompany, form]);

  return (
    <>
      <FormItem
        label={label}
        name="companyId"
        required={required}
        rules={[{ required }]}
        showLabel={false}
      >
        <SelectField<Company["id"][]>
          allowClear={allowClear}
          className={className}
          label={label}
          locked={disabled}
          onClear={handleRemove}
          onClick={disabled ? undefined : showModal}
          open={false}
          options={options}
          prefixIcon={
            <EntityTypeShapedIcon className={styles.entityIcon} entityType={EntityType.COMPANY} />
          }
          required={required}
          showArrow={false}
        />
      </FormItem>

      {modalVisible && (
        <CompanyAssociation
          allowAdd={allowAdd}
          assignedCompanies={associatedCompany ? [associatedCompany] : undefined}
          entityType={entityType}
          multiselect={false}
          onHide={hideModal}
          onSelect={handleSelect}
          suggestedCompanies={suggestedCompanies}
          zIndex={zIndex}
        />
      )}
    </>
  );
};

const mapDispatchToProps = {
  onFetchCompany: fetchCompany,
};

export default connect(null, mapDispatchToProps)(CompanyRelationshipField);
